'use client';

import Image from 'next/image';
import type { SessionMode } from '@jas/shared';
import { AnimateIn } from './animate-in';

const EXAMPLE_QUESTIONS = [
  'Why did you really disappear?',
  'What would you change about the whitepaper today?',
  'Explain the UTXO model from scratch',
  'What do you think of the Lightning Network?',
  'How should I think about fees right now?',
  'Did you always know Bitcoin would become this?',
  'Who do you think Satoshi Nakamoto actually is?',
  'Why proof-of-work and not proof-of-stake?',
];

interface IdleViewProps {
  onStart: () => void;
  mode: SessionMode;
  onModeChange: (m: SessionMode) => void;
}

export function IdleView({ onStart, mode, onModeChange }: IdleViewProps) {
  const ctaCopy = mode === 'voice' ? 'TALK TO SATOSHI' : 'START CHAT';
  const subCopy =
    mode === 'voice'
      ? '5 FREE MINUTES · NO ACCOUNT NEEDED'
      : '~10 FREE MESSAGES · NO ACCOUNT NEEDED';
  const bottomSubCopy =
    mode === 'voice'
      ? '5 FREE MINUTES · PER DAY · MORE COMING SOON'
      : '~10 FREE MESSAGES · PER DAY · MORE COMING SOON';
  return (
    <div className="flex flex-col pb-32 pt-20">

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="px-6 md:px-16 pt-12 pb-20 max-w-[1200px] mx-auto w-full">
        <div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-12 md:gap-8">

          {/* Left copy */}
          <div className="flex flex-col items-start md:max-w-[520px]">
            <AnimateIn>
              <h1 className="text-5xl md:text-6xl lg:text-[72px] font-semibold tracking-tight text-on-surface leading-[1.05] mb-5 mt-4">
                Satoshi never
                <br />
                <span className="text-primary">logged off.</span>
              </h1>
            </AnimateIn>
            <AnimateIn delay={1}>
              <p className="text-xl font-light text-on-surface-variant tracking-tight mb-6">
                He just stopped posting.
              </p>
            </AnimateIn>
            <AnimateIn delay={2}>
              <p className="text-base text-on-surface-variant leading-relaxed mb-10">
                Every BitcoinTalk thread, every private email, the whitepaper — distilled into a
                voice that knows exactly what he meant. Ask the question you&apos;ve been
                sitting on for years.
              </p>
            </AnimateIn>
            <AnimateIn delay={3}>
              <ModeToggle value={mode} onChange={onModeChange} />
              <button
                onClick={onStart}
                className="btn-cta-glow bg-primary-bright text-on-primary font-mono font-bold text-sm tracking-widest px-10 py-4 hover:bg-primary active:scale-95 transition-all mb-4"
              >
                {ctaCopy}
              </button>
              <p className="font-mono text-[10px] tracking-widest text-outline">
                {subCopy}
              </p>
            </AnimateIn>
          </div>

          {/* Right sculpture */}
          <AnimateIn className="relative flex items-end justify-center md:justify-end shrink-0 md:w-[420px] lg:w-[480px]">
            <div
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-24 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(ellipse, rgba(247,147,26,0.18) 0%, transparent 70%)' }}
            />
            <Image
              src="/satoshi-sculpture.png"
              alt="A faceless figure working on a laptop"
              width={994}
              height={1135}
              priority
              draggable={false}
              className="relative z-10 w-full max-w-[360px] md:max-w-full object-contain select-none"
              style={{ filter: 'drop-shadow(0 8px 32px rgba(247,147,26,0.12))' }}
            />
          </AnimateIn>

        </div>
      </section>

      <div className="border-t border-outline-variant mx-6 md:mx-16" />

      {/* ── What makes it real ───────────────────────────────────────── */}
      <section className="px-6 md:px-16 py-16 max-w-[1200px] mx-auto w-full">
        <AnimateIn>
          <p className="font-mono text-[10px] tracking-widest text-outline mb-12">
            WHAT MAKES IT REAL
          </p>
        </AnimateIn>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-outline-variant border border-outline-variant">
          {[
            {
              label: 'THE SOURCE MATERIAL',
              body: 'Not trained on Wikipedia summaries. Every BitcoinTalk post from 2008–2010, the whitepaper, and private emails with Hal Finney and Martti Malmi. When he quotes himself, he&apos;s actually quoting himself.',
            },
            {
              label: 'THE NETWORK, NOW',
              body: 'Current block height, real fee estimates, live mempool depth. He knows what&apos;s happening on the chain today — not just how it worked in 2009.',
            },
            {
              label: 'ACTUALLY VOICE',
              body: 'No chat window. No typing. You speak, he answers. The way a real conversation is supposed to work — and the way Satoshi, of all people, would probably prefer.',
            },
          ].map(({ label, body }, i) => (
            <AnimateIn
              key={label}
              delay={(i + 1) as 1 | 2 | 3}
            >
              <div className="bg-surface p-8 text-left h-full group hover:bg-surface-container-low transition-colors cursor-default">
                <p className="font-mono text-[10px] tracking-widest text-primary mb-4">{label}</p>
                <p className="text-sm text-on-surface-variant group-hover:text-on-surface transition-colors leading-relaxed">{body}</p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      <div className="border-t border-outline-variant mx-6 md:mx-16" />

      {/* ── What to ask ──────────────────────────────────────────────── */}
      <section id="what-to-ask" className="px-6 md:px-16 py-16 max-w-[1200px] mx-auto w-full">
        <AnimateIn className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <p className="font-mono text-[10px] tracking-widest text-outline mb-3">THINGS TO ASK</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-on-surface tracking-tight">
              Not sure where to start?
            </h2>
          </div>
          <p className="text-sm text-on-surface-variant max-w-xs leading-relaxed">
            These are real questions worth asking. Most AIs won&apos;t give you a straight answer.
            This one will.
          </p>
        </AnimateIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-outline-variant border border-outline-variant">
          {EXAMPLE_QUESTIONS.map((q, i) => (
            <AnimateIn key={q} delay={((i % 4) + 1) as 1 | 2 | 3 | 4}>
              <button
                onClick={onStart}
                className="bg-surface text-left px-6 py-5 group hover:bg-surface-container-low active:bg-surface-container transition-colors w-full"
              >
                <div className="flex items-start justify-between gap-4">
                  <p className="text-sm text-on-surface-variant group-hover:text-on-surface transition-colors leading-relaxed">
                    {q}
                  </p>
                  <span className="font-mono text-[10px] text-outline group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-0.5 inline-block">
                    &#8594;
                  </span>
                </div>
              </button>
            </AnimateIn>
          ))}
        </div>
      </section>

      <div className="border-t border-outline-variant mx-6 md:mx-16" />

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section className="flex flex-col items-center text-center px-6 py-24 max-w-[1200px] mx-auto w-full">
        <AnimateIn>
          <p className="font-mono text-[10px] tracking-widest text-outline mb-8">READY WHEN YOU ARE</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-on-surface tracking-tight mb-4 max-w-lg">
            Ask the question you&apos;ve had for years.
          </h2>
          <p className="text-sm text-on-surface-variant max-w-md leading-relaxed mb-10">
            Five minutes, no account, no judgment. Just the most interesting conversation
            you&apos;ll have about Bitcoin.
          </p>
          <button
            onClick={onStart}
            className="btn-cta-glow bg-primary-bright text-on-primary font-mono font-bold text-sm tracking-widest px-12 py-4 hover:bg-primary active:scale-95 transition-all mb-5"
          >
            {ctaCopy}
          </button>
          <p className="font-mono text-[10px] tracking-widest text-outline">
            {bottomSubCopy}
          </p>
        </AnimateIn>
      </section>

    </div>
  );
}

/**
 * Tight segmented control for picking between Voice and Chat before
 * starting a session. Renders as a pill of two mono labels — matches
 * Promit's existing nav language (font-mono tracking-widest) so it
 * doesn't feel bolted on. State is owned by the parent (VoicePanel);
 * this is presentation-only.
 */
function ModeToggle({
  value,
  onChange,
}: {
  value: SessionMode;
  onChange: (m: SessionMode) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Choose how to talk to Satoshi"
      className="inline-flex border border-outline-variant mb-5 select-none"
    >
      <ModeButton label="VOICE" active={value === 'voice'} onClick={() => onChange('voice')} />
      <ModeButton label="CHAT" active={value === 'chat'} onClick={() => onChange('chat')} />
    </div>
  );
}

function ModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`font-mono text-[11px] tracking-widest px-4 py-2 transition-colors ${
        active
          ? 'bg-primary-bright text-on-primary font-bold'
          : 'text-on-surface-variant hover:text-on-surface'
      }`}
    >
      {label}
    </button>
  );
}
